/**
 * ProtocolContactsEditor — ADR-141 §2/§4 notification-tree editor.
 *
 * Renders as a "Contacts" SectionCard in the Protocol drawer, directly
 * below the ADR-116 Recipients card. Same three-level vocabulary
 * (levels 0/1/2, drill as an independent axis) applied to NON-USER
 * recipients: directory contacts and contact tags.
 *
 * Tree semantics (ADR-141 §2):
 *   - a rule targets exactly ONE of (contact | tag);
 *   - `alert_min_level` places the rule in a level group — Level 0
 *     fires at alert start, 1/2 fire as the alert escalates through
 *     the `(from, to]` delta;
 *   - `drill_min_level` is the independent drill axis. This editor's
 *     add flow exposes it as a toggle that mirrors the chosen level
 *     (ADR-116 parity without a second level picker);
 *   - optional `channel_codes` restrict delivery to a subset of the
 *     contact's enabled destinations (filters only shrink). `app` is
 *     excluded — it rides the user-push path and is not restrictable
 *     from the tree.
 *
 * Append-only writes: POST for an existing target closes the old row
 * and returns a NEW id; DELETE closes. Rule ids are therefore never
 * cached — every write invalidates the per-type key root and the list
 * refetches (`useContactRules.ts`, rubric discipline).
 *
 * Gating: the whole card hides when the MASS_NOTIFICATION_CONTACTS
 * feature (flag + Module license, computed server-side) is off or the
 * caller lacks both `contacts.view_alerttypecontactrule` and
 * `contacts.change_alerttypecontactrule`; view-only callers get a
 * read-only list (no add / remove affordances).
 */
import type { TFunction } from 'i18next';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Box,
  Button,
  Center,
  Flex,
  IconButton,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { HiTrash } from 'react-icons/hi2';
import {
  Button as ButtonComponent,
  SectionCard,
  SelectInput,
  ToggleButton,
  ToggleGroup,
  type ToggleOption,
} from '@themes/aegix';
import { useFeature } from '../../../hooks/useFeature';
import { FeatureCode } from '../../../types/features';
import { useHasPermissions } from '../../../utils/hasPermission';
import {
  useContactRules,
  useCreateContactRule,
  useDeleteContactRule,
} from '../../../hooks/queries/useContactRules';
import { useContacts } from '../../../hooks/queries/useContacts';
import { useContactTags } from '../../../hooks/queries/useContactTags';
import {
  ADMIN_SELECTABLE_CHANNEL_TYPES,
  channelLabel,
} from '../../../services/contacts.service';
import type {
  AlertTypeContactRule,
  AlertTypeContactRuleWritePayload,
} from '../../../services/contactRules.service';
import { toaster } from '../../ui/toaster';

/** Levels visible to v1 SMS authors — same set as the Recipients grid
 *  (ADR-117 §"v1 scope"). */
export const TREE_LEVELS = [0, 1, 2] as const;
export type TreeLevel = (typeof TREE_LEVELS)[number];

/** Group heading per the ADR-141 §4 surface spec. */
export function treeLevelTitle(level: TreeLevel, t?: TFunction): string {
  if (t) {
    return level === 0
      ? t('protocols.treeLevel0Title', 'Level 0 — Alert start')
      : t('protocols.treeLevelTitle', 'Level {{level}}', { level });
  }
  return level === 0 ? 'Level 0 — Alert start' : `Level ${level}`;
}

/**
 * The level group a rule renders under. Grouping follows
 * `alert_min_level`; a drill-only rule (alert axis NULL) falls back to
 * its drill level so it stays visible. Out-of-range values clamp into
 * the v1 window.
 */
export function ruleLevel(rule: AlertTypeContactRule): TreeLevel {
  const raw = rule.alert_min_level ?? rule.drill_min_level ?? 0;
  if (raw <= 0) return 0;
  if (raw >= 2) return 2;
  return 1;
}

function ruleTargetLabel(rule: AlertTypeContactRule): string {
  return (rule.contact != null ? rule.contact_name : rule.tag_name) || '';
}

/** Rules bucketed by level, name-sorted within each bucket. */
export function groupRulesByLevel(
  rules: readonly AlertTypeContactRule[],
): Record<TreeLevel, AlertTypeContactRule[]> {
  const groups: Record<TreeLevel, AlertTypeContactRule[]> = {
    0: [],
    1: [],
    2: [],
  };
  for (const rule of rules) {
    groups[ruleLevel(rule)].push(rule);
  }
  for (const level of TREE_LEVELS) {
    groups[level].sort((a, b) =>
      ruleTargetLabel(a).localeCompare(ruleTargetLabel(b), undefined, {
        sensitivity: 'base',
      }),
    );
  }
  return groups;
}

export interface TreeRuleDraft {
  alertTypeId: number;
  /** Exactly one target — enforced by the discriminated union. */
  target: { kind: 'contact'; id: number } | { kind: 'tag'; id: number };
  level: TreeLevel;
  /** Drill toggle: mirrors the chosen level onto the drill axis. */
  includeDrill: boolean;
  /** Empty = no restriction (all enabled destinations). */
  channelCodes: readonly string[];
}

export function buildRulePayload(
  draft: TreeRuleDraft,
): AlertTypeContactRuleWritePayload {
  return {
    alert_type: draft.alertTypeId,
    contact: draft.target.kind === 'contact' ? draft.target.id : null,
    tag: draft.target.kind === 'tag' ? draft.target.id : null,
    alert_min_level: draft.level,
    drill_min_level: draft.includeDrill ? draft.level : null,
    ...(draft.channelCodes.length > 0
      ? { channel_codes: [...draft.channelCodes] }
      : {}),
  };
}

interface ProtocolContactsEditorProps {
  alertTypeId: number | null;
}

export function ProtocolContactsEditor({
  alertTypeId,
}: ProtocolContactsEditorProps) {
  const { t } = useTranslation();
  const featureEnabled = useFeature(FeatureCode.MASS_NOTIFICATION_CONTACTS);
  const canView = useHasPermissions(['contacts.view_alerttypecontactrule']);
  const canChange = useHasPermissions(['contacts.change_alerttypecontactrule']);

  if (!featureEnabled || (!canView && !canChange)) {
    return null;
  }

  if (alertTypeId == null) {
    return (
      <SectionCard
        title={t('protocols.contactsSectionTitle', 'Contacts')}
        e2eTitle="contactsTreeSection"
      >
        <Text fontSize="sm" color="text.secondary">
          {t(
            'protocols.saveProtocolFirstContacts',
            'Save the protocol first — contact notifications can be configured once the protocol has been created.',
          )}
        </Text>
      </SectionCard>
    );
  }

  return <ContactsTreeCard alertTypeId={alertTypeId} readOnly={!canChange} />;
}

function ContactsTreeCard({
  alertTypeId,
  readOnly,
}: {
  alertTypeId: number;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: rules = [], isLoading } = useContactRules(alertTypeId);
  const { data: contacts = [] } = useContacts();
  const { data: tags = [] } = useContactTags();
  const createMut = useCreateContactRule(alertTypeId);
  const deleteMut = useDeleteContactRule(alertTypeId);

  const levelOptions: ToggleOption[] = useMemo(
    () =>
      TREE_LEVELS.map((level) => ({
        value: String(level),
        label: treeLevelTitle(level, t),
        'data-e2e': `treeLevelOption-${level}`,
      })),
    [t],
  );

  const [isAdding, setIsAdding] = useState(false);
  const [targetMode, setTargetMode] = useState<'contact' | 'tag'>('contact');
  const [contactId, setContactId] = useState<number | null>(null);
  const [tagId, setTagId] = useState<number | null>(null);
  const [level, setLevel] = useState<TreeLevel>(0);
  const [includeDrill, setIncludeDrill] = useState(false);
  const [channelCodes, setChannelCodes] = useState<string[]>([]);

  const resetAddForm = () => {
    setIsAdding(false);
    setTargetMode('contact');
    setContactId(null);
    setTagId(null);
    setLevel(0);
    setIncludeDrill(false);
    setChannelCodes([]);
  };

  const contactOptions = useMemo(
    () =>
      contacts
        .filter((c) => c.is_active !== false)
        .sort((a, b) =>
          a.display_name.localeCompare(b.display_name, undefined, {
            sensitivity: 'base',
          }),
        )
        .map((c) => ({ label: c.display_name, value: String(c.id) })),
    [contacts],
  );
  const tagOptions = useMemo(
    () => tags.map((t) => ({ label: t.name, value: String(t.id) })),
    [tags],
  );

  const grouped = useMemo(() => groupRulesByLevel(rules), [rules]);
  const hasRules = rules.length > 0;
  const isMutating = createMut.isPending || deleteMut.isPending;

  const selectedTargetId = targetMode === 'contact' ? contactId : tagId;
  const canSave = selectedTargetId != null && !isMutating;

  const toggleChannel = (code: string) => {
    setChannelCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  };

  const handleAdd = async () => {
    if (selectedTargetId == null) return;
    const payload = buildRulePayload({
      alertTypeId,
      target:
        targetMode === 'contact'
          ? { kind: 'contact', id: selectedTargetId }
          : { kind: 'tag', id: selectedTargetId },
      level,
      includeDrill,
      channelCodes,
    });
    try {
      await createMut.mutateAsync(payload);
      resetAddForm();
    } catch {
      toaster.create({
        title: t(
          'protocols.failedAddContactRule',
          'Failed to add contact rule',
        ),
        type: 'error',
      });
    }
  };

  const handleRemove = async (ruleId: number) => {
    try {
      await deleteMut.mutateAsync(ruleId);
    } catch {
      toaster.create({
        title: t(
          'protocols.failedRemoveContactRule',
          'Failed to remove contact rule',
        ),
        type: 'error',
      });
    }
  };

  const goToContacts = () => navigate('/account?tab=people&subtab=contacts');

  return (
    <SectionCard
      title={t('protocols.contactsSectionTitle', 'Contacts')}
      e2eTitle="contactsTreeSection"
    >
      {isLoading ? (
        <Center py={4}>
          <Spinner size="sm" />
        </Center>
      ) : (
        <Box
          opacity={isMutating ? 0.7 : 1}
          pointerEvents={isMutating ? 'none' : 'auto'}
        >
          {hasRules ? (
            <Flex direction="column" gap={3}>
              {TREE_LEVELS.map((groupLevel) => (
                <Box key={groupLevel} data-e2e={`treeLevelGroup-${groupLevel}`}>
                  <Text
                    fontSize="sm"
                    fontWeight={600}
                    color="text.primary"
                    mb={1}
                  >
                    {treeLevelTitle(groupLevel, t)}
                  </Text>
                  {grouped[groupLevel].length === 0 ? (
                    <Text
                      fontSize="xs"
                      color="text.tertiary"
                      fontStyle="italic"
                    >
                      {t(
                        'protocols.noContactsAtLevel',
                        'No contacts or tags at this level.',
                      )}
                    </Text>
                  ) : (
                    <Flex direction="column" gap={1}>
                      {grouped[groupLevel].map((rule) => (
                        <Flex
                          key={rule.id}
                          align="center"
                          gap={2}
                          borderWidth="1px"
                          borderColor="border.tertiary"
                          borderRadius="md"
                          px={2}
                          py={1}
                          data-e2e="treeRuleRow"
                        >
                          {rule.contact != null ? (
                            <Text
                              fontSize="sm"
                              color="text.primary"
                              data-e2e="treeRuleContactName"
                            >
                              {rule.contact_name}
                            </Text>
                          ) : (
                            <Badge
                              variant="subtle"
                              colorPalette="blue"
                              data-e2e="treeRuleTagChip"
                            >
                              {rule.tag_name}
                            </Badge>
                          )}
                          {rule.drill_min_level != null && (
                            <Badge
                              variant="outline"
                              colorPalette="purple"
                              data-e2e="treeRuleDrillBadge"
                            >
                              {t('protocols.drillBadge', 'Drill')}
                            </Badge>
                          )}
                          {(rule.channel_codes ?? []).map((code) => (
                            <Badge
                              key={code}
                              variant="subtle"
                              colorPalette="gray"
                              data-e2e={`treeRuleChannelChip-${code}`}
                            >
                              {channelLabel(code)}
                            </Badge>
                          ))}
                          {!readOnly && (
                            <IconButton
                              aria-label={t(
                                'protocols.removeTargetAria',
                                'Remove {{target}}',
                                { target: ruleTargetLabel(rule) },
                              )}
                              size="xs"
                              variant="ghost"
                              colorPalette="red"
                              marginLeft="auto"
                              onClick={() => void handleRemove(rule.id)}
                              data-e2e={`removeTreeRule-${rule.id}`}
                            >
                              <HiTrash />
                            </IconButton>
                          )}
                        </Flex>
                      ))}
                    </Flex>
                  )}
                </Box>
              ))}
            </Flex>
          ) : (
            <Box data-e2e="treeEmptyState">
              <Text fontSize="sm" color="text.secondary" mb={2}>
                {t(
                  'protocols.noContactsWired',
                  'No contacts or tags are wired to this protocol yet. Contact rules page people who are not platform users — PIO lists, municipal partners, facility contractors — in the same three tiers as Recipients: Level 0 is notified when the alert starts; Levels 1 and 2 are notified as the alert escalates. Contacts and tags are managed under Account > People.',
                )}
              </Text>
              <ButtonComponent
                visual="secondary"
                size="sm"
                onClick={goToContacts}
                data-e2e="manageContactsButton"
              >
                {t('protocols.manageContacts', 'Manage Contacts')}
              </ButtonComponent>
            </Box>
          )}

          {!readOnly && !isAdding && (
            <Box mt={3}>
              <ButtonComponent
                visual="secondary"
                size="sm"
                onClick={() => setIsAdding(true)}
                data-e2e="addTreeRule"
              >
                {t('protocols.addContactOrTag', 'Add Contact or Tag')}
              </ButtonComponent>
            </Box>
          )}

          {!readOnly && isAdding && (
            <Flex
              direction="column"
              gap={3}
              mt={3}
              borderWidth="1px"
              borderColor="border.tertiary"
              borderRadius="md"
              p={3}
              data-e2e="treeAddForm"
            >
              <ToggleGroup
                options={[
                  {
                    value: 'contact',
                    label: t('protocols.contact', 'Contact'),
                    'data-e2e': 'treeTargetMode-contact',
                  },
                  {
                    value: 'tag',
                    label: t('protocols.tag', 'Tag'),
                    'data-e2e': 'treeTargetMode-tag',
                  },
                ]}
                value={targetMode}
                onChange={(v) => setTargetMode(v as 'contact' | 'tag')}
                fillWidth
                data-e2e="treeTargetModeToggle"
              />

              {targetMode === 'contact' ? (
                <SelectInput
                  label={t('protocols.contact', 'Contact')}
                  id="tree-contact-select"
                  e2eTitle="treeContactSelect"
                  placeholder={t(
                    'protocols.searchContactsPlaceholder',
                    'Search contacts…',
                  )}
                  option={contactOptions}
                  value={contactId != null ? String(contactId) : ''}
                  onChange={(v: string) => setContactId(v ? Number(v) : null)}
                />
              ) : (
                <SelectInput
                  label={t('protocols.tag', 'Tag')}
                  id="tree-tag-select"
                  e2eTitle="treeTagSelect"
                  placeholder={t(
                    'protocols.selectTagPlaceholder',
                    'Select a tag',
                  )}
                  option={tagOptions}
                  value={tagId != null ? String(tagId) : ''}
                  onChange={(v: string) => setTagId(v ? Number(v) : null)}
                />
              )}

              <Box>
                <Text
                  fontSize="sm"
                  fontWeight={600}
                  color="text.primary"
                  mb={1}
                >
                  {t('protocols.notifyAt', 'Notify at')}
                </Text>
                <ToggleGroup
                  options={levelOptions}
                  value={String(level)}
                  onChange={(v) => setLevel(Number(v) as TreeLevel)}
                  fillWidth
                  data-e2e="treeLevelToggleGroup"
                />
              </Box>

              <ToggleButton
                title={t(
                  'protocols.alsoNotifyDuringDrills',
                  'Also notify during drills',
                )}
                checked={includeDrill}
                onCheckedChange={() => setIncludeDrill((prev) => !prev)}
                e2eTitle="treeDrillToggle"
              />

              <Box>
                <Text
                  fontSize="sm"
                  fontWeight={600}
                  color="text.primary"
                  mb={1}
                >
                  {t(
                    'protocols.restrictChannelsOptional',
                    'Restrict channels (optional)',
                  )}
                </Text>
                <Text fontSize="xs" color="text.secondary" mb={1}>
                  {t(
                    'protocols.noSelectionSendsToAll',
                    'No selection sends to every enabled destination.',
                  )}
                </Text>
                <Flex wrap="wrap" gap={1}>
                  {ADMIN_SELECTABLE_CHANNEL_TYPES.map((channel) => {
                    const selected = channelCodes.includes(channel.code);
                    return (
                      <Button
                        key={channel.code}
                        unstyled
                        cursor="pointer"
                        aria-pressed={selected}
                        onClick={() => toggleChannel(channel.code)}
                        data-e2e={`treeChannelChip-${channel.code}`}
                      >
                        <Badge
                          variant={selected ? 'solid' : 'subtle'}
                          colorPalette={selected ? 'blue' : 'gray'}
                        >
                          {channel.label}
                        </Badge>
                      </Button>
                    );
                  })}
                </Flex>
              </Box>

              <Flex gap={2} justify="flex-end">
                <ButtonComponent
                  visual="secondary"
                  size="sm"
                  onClick={resetAddForm}
                  data-e2e="cancelTreeRule"
                >
                  {t('common.cancel', 'Cancel')}
                </ButtonComponent>
                <ButtonComponent
                  visual="primary"
                  size="sm"
                  disabled={!canSave}
                  onClick={() => void handleAdd()}
                  data-e2e="saveTreeRule"
                >
                  {t('common.add', 'Add')}
                </ButtonComponent>
              </Flex>
            </Flex>
          )}
        </Box>
      )}
    </SectionCard>
  );
}
